import { useMemo, useState } from 'react';
import { Droplet, FileUp, Gauge, Plus, Receipt, Route as RouteIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatMinor, formatNumber, formatWhen, isoDate } from '@/lib/format';
import {
  useAddFuel,
  useFuelProviders,
  useFuelReport,
  useFuelTransactions,
  useImportFuel,
  type FuelVehicleRow,
} from '@/api/hooks/finance';
import { useVehicles } from '@/api/hooks/catalog';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { Field, Input, Select } from '@/components/ui/Field';
import { ErrorState, LoadingBlock } from '@/components/ui/State';
import { Table, TBody, TD, TH, THead, TR, TableEmpty } from '@/components/ui/Table';
import { toast } from '@/stores/toast';
import { FuelCardsCard } from '@/components/fuel/FuelCardsCard';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  ГОРИВО — разходът по бусове.
 *
 *  Литрите и парите идват от служебните карти (импорт от AS24 и др. или
 *  ръчно). Километрите — от километража при зареждане, от Frotcom или от GPS
 *  позициите; до числото пише откъде е. Бус с разход над средното за парка
 *  с повече от 15 % се оцветява — там се гледа първо.
 * ═══════════════════════════════════════════════════════════════════════════
 */

type Period = 'thisMonth' | 'lastMonth' | 'last3' | 'thisYear';

function range(period: Period, now = new Date()): { from: string; to: string } {
  const y = now.getFullYear();
  const m = now.getMonth();
  const start = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
  switch (period) {
    case 'lastMonth':
      return { from: start(new Date(y, m - 1, 1)), to: start(new Date(y, m, 1)) };
    case 'last3':
      return { from: start(new Date(y, m - 2, 1)), to: start(new Date(y, m, now.getDate() + 1)) };
    case 'thisYear':
      return { from: start(new Date(y, 0, 1)), to: start(new Date(y, m, now.getDate() + 1)) };
    case 'thisMonth':
    default:
      return { from: start(new Date(y, m, 1)), to: start(new Date(y, m, now.getDate() + 1)) };
  }
}

const KM_SOURCE: Record<string, string> = {
  odometer: 'по километража при зареждане',
  telematics: 'по Frotcom',
  gps: 'по GPS',
};

export function FuelPage() {
  const [period, setPeriod] = useState<Period>('thisMonth');
  const { from, to } = useMemo(() => range(period), [period]);
  const report = useFuelReport(from, to);
  const [selected, setSelected] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [adding, setAdding] = useState(false);

  const fleetAvg = report.data?.totals.litresPer100 ?? null;

  return (
    <div className="flex min-h-full flex-col gap-4 p-3 sm:p-5">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">Гориво</h1>
          <p className="text-sm text-ink-dim">Разход по бусове — литри, пари, литри на 100 км.</p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Select
            className="w-44"
            aria-label="Период"
            value={period}
            onChange={(e) => setPeriod(e.target.value as Period)}
            options={[
              { value: 'thisMonth', label: 'Този месец' },
              { value: 'lastMonth', label: 'Миналия месец' },
              { value: 'last3', label: 'Последните 3 месеца' },
              { value: 'thisYear', label: 'Тази година' },
            ]}
          />
          <Button icon={<FileUp className="size-4" />} onClick={() => setImporting(true)}>
            Импорт от карта
          </Button>
          <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setAdding(true)}>
            Зареждане
          </Button>
        </div>
      </div>

      {report.isLoading ? (
        <LoadingBlock />
      ) : report.isError || !report.data ? (
        <ErrorState onRetry={() => void report.refetch()} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile icon={<Droplet className="size-5" />} label="Литри" value={formatNumber(report.data.totals.litres)} />
            <Tile icon={<Receipt className="size-5" />} label="Сума (EUR)" value={formatMinor(report.data.totals.costEurMinor, 'EUR')} />
            <Tile
              icon={<Gauge className="size-5" />}
              label="Среден разход"
              value={fleetAvg !== null ? `${fleetAvg.toFixed(1)} л/100 км` : '—'}
              accent
            />
            <Tile
              icon={<RouteIcon className="size-5" />}
              label="Цена на км"
              value={report.data.totals.costPerKmMinor !== null ? `${formatMinor(report.data.totals.costPerKmMinor, 'EUR')}` : '—'}
            />
          </div>

          <Card className="overflow-hidden">
            <CardHeader
              title="По бусове"
              subtitle="Натиснете бус, за да видите зарежданията му. Оцветен е разходът над средния за парка с 15 %."
            />
            <div className="overflow-x-auto">
              <Table>
                <THead>
                  <TR>
                    <TH>Бус</TH>
                    <TH numeric>Зареждания</TH>
                    <TH numeric>Литри</TH>
                    <TH numeric>Сума</TH>
                    <TH numeric>Изминати км</TH>
                    <TH numeric>л/100 км</TH>
                    <TH numeric>Цена/км</TH>
                    <TH>Последно</TH>
                  </TR>
                </THead>
                <TBody>
                  {report.data.vehicles.length === 0 ? (
                    <TableEmpty colSpan={8}>Няма бусове.</TableEmpty>
                  ) : (
                    report.data.vehicles.map((row) => (
                      <FuelRow
                        key={row.vehicleId}
                        row={row}
                        fleetAvg={fleetAvg}
                        active={selected === row.vehicleId}
                        onClick={() => setSelected(selected === row.vehicleId ? null : row.vehicleId)}
                      />
                    ))
                  )}
                </TBody>
              </Table>
            </div>
          </Card>

          {selected ? <Transactions from={from} to={to} vehicleId={selected} /> : null}

          <FuelCardsCard />

          {report.data.totals.litres === 0 ? (
            <p className="text-sm text-ink-dim">
              Още няма зареждания за периода. Качете месечния файл от горивната карта с „Импорт от карта“ или въведете
              зареждане ръчно.
            </p>
          ) : null}
        </>
      )}

      {importing ? <ImportDialog onClose={() => setImporting(false)} /> : null}
      {adding ? <AddFuelDialog onClose={() => setAdding(false)} /> : null}
    </div>
  );
}

function Tile({ icon, label, value, accent = false }: { icon: React.ReactNode; label: string; value: string; accent?: boolean }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-sm text-ink-dim">
        {icon}
        {label}
      </div>
      <div className={cn('mt-2 font-display text-2xl font-bold tabular', accent && 'text-accent')}>{value}</div>
    </Card>
  );
}

function FuelRow({ row, fleetAvg, active, onClick }: { row: FuelVehicleRow; fleetAvg: number | null; active: boolean; onClick: () => void }) {
  const high = row.litresPer100 !== null && fleetAvg !== null && row.litresPer100 > fleetAvg * 1.15;
  return (
    <TR className={cn('cursor-pointer', active && 'bg-accent-soft')} onClick={onClick}>
      <TD>
        <div className="font-display text-sm font-semibold">{row.fleetNo}</div>
        <div className="font-mono text-[11px] text-ink-faint">{row.plate}</div>
      </TD>
      <TD numeric>{row.fills || '—'}</TD>
      <TD numeric>{row.litres ? formatNumber(row.litres) : '—'}</TD>
      <TD numeric>
        {row.costEurMinor ? formatMinor(row.costEurMinor, 'EUR') : '—'}
        {row.otherCurrencyFills > 0 ? (
          <div className="text-[10px] text-ink-faint">+{row.otherCurrencyFills} в друга валута</div>
        ) : null}
      </TD>
      <TD numeric>
        {row.km ? formatNumber(row.km) : '—'}
        {row.kmSource ? <div className="text-[10px] text-ink-faint">{KM_SOURCE[row.kmSource]}</div> : null}
      </TD>
      <TD numeric>
        {row.litresPer100 !== null ? (
          <span className={cn('font-medium', high ? 'text-warn' : 'text-ink')}>{row.litresPer100.toFixed(1)}</span>
        ) : (
          '—'
        )}
        {row.telematicsLitresPer100 !== null ? (
          <div className="text-[10px] text-ink-faint">двигател: {row.telematicsLitresPer100.toFixed(1)}</div>
        ) : null}
      </TD>
      <TD numeric>{row.costPerKmMinor !== null ? formatMinor(row.costPerKmMinor, 'EUR') : '—'}</TD>
      <TD className="whitespace-nowrap text-ink-dim">
        {row.lastFillAt ? formatWhen(row.lastFillAt) : '—'}
        {row.anomalies > 0 ? (
          <Badge tone="warn" className="ml-1.5">
            {row.anomalies} за проверка
          </Badge>
        ) : null}
      </TD>
    </TR>
  );
}

function Transactions({ from, to, vehicleId }: { from: string; to: string; vehicleId: string }) {
  const tx = useFuelTransactions(from, to, vehicleId);
  return (
    <Card className="overflow-hidden">
      <CardHeader title="Зареждания на буса" subtitle="Най-новите отгоре" />
      {tx.isLoading ? (
        <LoadingBlock />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <THead>
              <TR>
                <TH>Кога</TH>
                <TH>Къде</TH>
                <TH numeric>Литри</TH>
                <TH numeric>Сума</TH>
                <TH numeric>Километраж</TH>
                <TH>Източник</TH>
              </TR>
            </THead>
            <TBody>
              {(tx.data ?? []).length === 0 ? (
                <TableEmpty colSpan={6}>Няма зареждания за периода.</TableEmpty>
              ) : (
                (tx.data ?? []).map((t) => (
                  <TR key={t.id}>
                    <TD className="whitespace-nowrap">{formatWhen(t.transactedAt)}</TD>
                    <TD className="max-w-[16rem] truncate">
                      {t.station ?? '—'}
                      {t.country ? <span className="ml-1 text-ink-faint">({t.country})</span> : null}
                    </TD>
                    <TD numeric>{t.litres !== null ? t.litres.toFixed(1) : '—'}</TD>
                    <TD numeric>{formatMinor(t.grossMinor, t.ccy)}</TD>
                    <TD numeric>{t.odometerKm !== null ? formatNumber(t.odometerKm) : '—'}</TD>
                    <TD>
                      {t.provider === 'manual' ? 'ръчно' : t.provider.toUpperCase()}
                      {t.anomalyFlags.length > 0 ? (
                        <Badge tone="warn" className="ml-1.5" title={t.anomalyFlags.join(', ')}>
                          за проверка
                        </Badge>
                      ) : null}
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        </div>
      )}
    </Card>
  );
}

function ImportDialog({ onClose }: { onClose: () => void }) {
  const providers = useFuelProviders();
  const importFuel = useImportFuel();
  const [provider, setProvider] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const list = providers.data?.providers ?? [];
  const chosen = provider || list[0]?.key || '';

  return (
    <Dialog open onClose={onClose} title="Импорт от горивна карта" width="md">
      <div className="space-y-3">
        <p className="text-sm text-ink-dim">
          Изтеглете справката за транзакциите от AS 24 Online (или от сайта на другата карта) като CSV и я качете тук.
          Ако сайтът дава Excel — отворете го и „Запиши като" CSV. Повторно качен ред не се записва втори път.
        </p>
        <Field label="Карта">
          <Select value={chosen} onChange={(e) => setProvider(e.target.value)} options={list.map((p) => ({ value: p.key, label: p.label }))} />
        </Field>
        <Field label="Файл (CSV)">
          <Input type="file" accept=".csv,text/csv,text/plain" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Отказ
          </Button>
          <Button
            variant="primary"
            disabled={!file || !chosen}
            loading={importFuel.isPending}
            onClick={async () => {
              if (!file) return;
              const content = await file.text();
              importFuel.mutate(
                { provider: chosen, content, createExpenses: true },
                {
                  onSuccess: (r) => {
                    const anomalies = r.anomalies?.length ?? 0;
                    const notify = r.unmatchedCards.length || anomalies ? toast.warn : toast.ok;
                    notify(
                      `Записани ${r.imported} зареждания`,
                      [
                        r.duplicates ? `${r.duplicates} вече бяха качени` : null,
                        r.failed.length ? `${r.failed.length} реда не се прочетоха` : null,
                        r.unmatchedCards.length
                          ? `непознати карти: ${r.unmatchedCards.join(', ')} — добавете ги в „Горивни карти"`
                          : null,
                        anomalies ? `${anomalies} за проверка — вижте „Аномалии"` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || undefined,
                    );
                    onClose();
                  },
                },
              );
            }}
          >
            Качи
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function AddFuelDialog({ onClose }: { onClose: () => void }) {
  const vehicles = useVehicles();
  const add = useAddFuel();
  const [form, setForm] = useState({ vehicleId: '', when: isoDate(new Date()), litres: '', amount: '', ccy: 'EUR', odometer: '', station: '' });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const litres = Number(form.litres.replace(',', '.'));
  const grossMinor = Math.round(Number(form.amount.replace(',', '.')) * 100);
  const valid = form.vehicleId && litres > 0 && grossMinor > 0;

  return (
    <Dialog open onClose={onClose} title="Ръчно зареждане" width="md">
      <form
        className="grid grid-cols-2 gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid) return;
          add.mutate(
            {
              vehicleId: form.vehicleId,
              transactedAt: new Date(`${form.when}T12:00:00`).toISOString(),
              litres,
              grossMinor,
              ccy: form.ccy,
              ...(form.odometer ? { odometerKm: Number(form.odometer) } : {}),
              ...(form.station.trim() ? { station: form.station.trim() } : {}),
            },
            {
              onSuccess: () => {
                toast.ok('Зареждането е записано.');
                onClose();
              },
            },
          );
        }}
      >
        <Field label="Бус" required className="col-span-2">
          <Select
            value={form.vehicleId}
            onChange={set('vehicleId')}
            options={[{ value: '', label: '— изберете —' }, ...(vehicles.data?.items ?? []).map((v) => ({ value: v.id, label: `${v.fleetNo} · ${v.plate}` }))]}
          />
        </Field>
        <Field label="Дата">
          <Input type="date" value={form.when} onChange={set('when')} />
        </Field>
        <Field label="Литри" required>
          <Input inputMode="decimal" value={form.litres} onChange={set('litres')} placeholder="62,5" />
        </Field>
        <Field label="Сума" required>
          <Input inputMode="decimal" value={form.amount} onChange={set('amount')} placeholder="95,20" />
        </Field>
        <Field label="Валута">
          <Select value={form.ccy} onChange={set('ccy')} options={['EUR', 'BGN', 'HUF', 'RON', 'CZK', 'PLN', 'CHF'].map((c) => ({ value: c, label: c }))} />
        </Field>
        <Field label="Километраж" hint="Нужен е за литрите на 100 км">
          <Input inputMode="numeric" value={form.odometer} onChange={set('odometer')} placeholder="201 500" />
        </Field>
        <Field label="Бензиностанция">
          <Input value={form.station} onChange={set('station')} />
        </Field>
        <div className="col-span-2 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Отказ
          </Button>
          <Button type="submit" variant="primary" disabled={!valid} loading={add.isPending}>
            Запиши
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
